import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import m1 from '../../../images/clustermarker/m1.png';
import m2 from '../../../images/clustermarker/m2.png';
import m3 from '../../../images/clustermarker/m3.png';
import m4 from '../../../images/clustermarker/m4.png';
import m5 from '../../../images/clustermarker/m5.png';
import styles from './Map.module.css';
import moment from 'moment';
import { PRODUCT_TYPES, CLASSIC_PANEL_STATUS } from '../../../constants/enums.js';
import QueryStringService from '../../../services/queryStringService.js';
import dateFunctions from '../../../utils/dateFunctions.js';
import campaignValues from '../../../utils/campaignValues.js';
import { useError } from '../Contexts/ErrorContext';
import { useLoading } from '../Contexts/LoadingContext';
import {
    registerMapInstance,
    debugMapInstance
} from './mapService';


const GOOGLE_MAPS_API_KEY = "AIzaSyCBZpaGoUvXlsgS93QmUNdw0rjT1eEyrT4";
const DEFAULT_CENTER = { lat: 59.4070124, lng: 11.9221516 };
const MAX_ZOOM_LEVEL = 19;
const DEFAULT_ZOOM = 10;
const MARKER_SIZE = { width: '20px', height: '20px' };
const MAP_OPTIONS = {
    mapId: '2936f547e40f459c',
    disableDefaultUI: true,
    zoomControl: true,
};

const CLUSTER_SIZES = {
    SMALL: { max: 10, icon: m1, size: { width: 53, height: 53 } },
    MEDIUM: { max: 20, icon: m2, size: { width: 56, height: 56 } },
    LARGE: { max: 50, icon: m3, size: { width: 66, height: 66 } },
    XLARGE: { max: 100, icon: m4, size: { width: 78, height: 78 } },
    XXLARGE: { icon: m5, size: { width: 90, height: 90 } }
};

const getClusterIcon = (count) => {
    if (count < CLUSTER_SIZES.SMALL.max) return CLUSTER_SIZES.SMALL;
    if (count < CLUSTER_SIZES.MEDIUM.max) return CLUSTER_SIZES.MEDIUM;
    if (count < CLUSTER_SIZES.LARGE.max) return CLUSTER_SIZES.LARGE;
    if (count < CLUSTER_SIZES.XLARGE.max) return CLUSTER_SIZES.XLARGE;
    return CLUSTER_SIZES.XXLARGE;
};

const yearFormatOption = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false
};

const createClusterElement = (() => {
    const cache = new Map();

    return (count) => {
        if (cache.has(count)) {
            return cache.get(count).cloneNode(true);
        }

        const clusterConfig = getClusterIcon(count);
        const container = document.createElement('div');
        Object.assign(container.style, {
            position: 'relative',
            width: `${clusterConfig.size.width}px`,
            height: `${clusterConfig.size.height}px`,
        });

        const image = document.createElement('img');
        Object.assign(image.style, {
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0
        });
        image.src = clusterConfig.icon;

        const label = document.createElement('div');
        Object.assign(label.style, {
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: 'Arial,sans-serif',
            pointerEvents: 'none'
        });
        label.textContent = String(count);

        container.appendChild(image);
        container.appendChild(label);

        cache.set(count, container);
        return container.cloneNode(true);
    };
})();

const createMarkerElement = (() => {
    const cache = new Map();

    return (color) => {
        if (cache.has(color)) {
            return cache.get(color).cloneNode(true);
        }

        const element = document.createElement('div');
        Object.assign(element.style, {
            backgroundColor: color || '#72BF44',
            width: MARKER_SIZE.width,
            height: MARKER_SIZE.height,
            borderRadius: '50%',
            border: '1px solid #000'
        });

        cache.set(color, element);
        return element.cloneNode(true);
    };
})();

const PanelMap = React.memo(({
    campaignType,
    campaignPromiseType,
    orderId,
    customerId,
    orderlineIds,
    panels = [],
    initialCenter = null,
    initialZoom = null,
    isLoadedMap = false,
    onSaveMap = null, // Callback to parent component when map is saved
}) => {
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const mapInstanceRef = useRef(null);
    const infoWindowRef = useRef(null);
    const clustererRef = useRef(null);
    const loaderRef = useRef(null);
    const queryStringService = useMemo(() => new QueryStringService(), []);
    const countryCode = useMemo(() => queryStringService.getCurrentCountry(), [queryStringService]);
    const { showError } = useError();
    const isNonMOMSFollowUp = useMemo(() =>
        campaignValues.isNonMOMSFollowUpCampaign(campaignPromiseType),
        [campaignPromiseType]
    );

    const validPanels = useMemo(() =>
        panels.filter(panel => panel.latitude && panel.longitude),
        [panels]
    );

    const createInfoWindowContent = useCallback((panel) => {
        const panelStatuses = panel.panelIdsAtLocation?.map(id => {
            const statuses = panel.statusInformation?.map(info => {
                if (info.status && !info.timeStamp && !info.design) {
                    return `<span><b>Status: </b>${info.status || ''}</span>`;
                }

                return `
                <div style="margin: 5px 0;">
                    <b>Status: </b>${info.status || ''} 
                    ${info.timeStamp && !isNonMOMSFollowUp ? `<b>Time: </b>${dateFunctions.getCountrySpecificDateTimeFormatFromDate(countryCode, moment(info.timeStamp))}` : ''}
                    ${info.design ? `<br/><b>Design: </b>${info.design}` : ''}
                </div>
            `;
            }).join('') || 'No status information';

            if (panel.statusInformation?.every(info => info.status && !info.timeStamp && !info.design)) {
                return `<div style="margin-bottom: 10px;"><b>Panel-id: </b>${id} ${statuses}</div>`;
            }

            return `
            <div style="margin-bottom: 10px;">
                <b>Panel-id: </b>${id}
                <div style="margin-left: 10px;">${statuses}</div>
            </div>`;
        }).join('');


        const incidents = (panel.statusInformation || [])
            .filter(status => status.incidents)
            .map(status => status.incidents.map(incident => `
            <div>
                <br/>
                <b>${dateFunctions.getCountrySpecificDateTimeFormatFromDate(countryCode, moment(incident.periodStart), yearFormatOption)} - ${dateFunctions.getCountrySpecificDateTimeFormatFromDate(countryCode, moment(incident.periodEnd), yearFormatOption)}:</b>
                <br/>
                ${incident.note}
            </div>
        `).join('')).join('');

        return `
        <div class="infoWindowContent" style="font-family: 'BauerMediaSans', sans-serif; font-size: 12px;">
            ${panelStatuses}
            <div><b>Address: </b>${panel.address || ''} ${panel.city || ''}</div>
            ${incidents}
        </div>
    `;
    }, [countryCode, isNonMOMSFollowUp]);

    const customRenderer = useMemo(() => ({
        render({ count, position, markers }) {
            const cluster = new google.maps.marker.AdvancedMarkerElement({
                position,
                content: createClusterElement(count)
            });

            if (markers.length > 1) {
                cluster.addListener('click', () => {
                    const bounds = new google.maps.LatLngBounds();
                    markers.forEach(marker => bounds.extend(marker.position));
                    cluster.map.fitBounds(bounds);

                    google.maps.event.addListenerOnce(cluster.map, 'bounds_changed', () => {
                        if (cluster.map.getZoom() > MAX_ZOOM_LEVEL) {
                            cluster.map.setZoom(MAX_ZOOM_LEVEL);
                        }
                    });
                });
            }

            return cluster;
        }
    }), []);

    const initializeMap = useCallback(async () => {
        if (!loaderRef.current) {
             loaderRef.current = new Loader({
                apiKey: GOOGLE_MAPS_API_KEY,
                version: "weekly",
                libraries: ["marker"] 
            });
        }

        try {
            const google = await loaderRef.current.load();
            const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

            if (!mapRef.current) {
                 showError("Map container element missing.");
                 return; 
            }

            // Create map instance only if it doesn't exist
            if (!mapInstanceRef.current) {
                const center = initialCenter
                    ? initialCenter
                    : validPanels.length > 0 
                        ? { lat: validPanels[0].latitude, lng: validPanels[0].longitude }
                        : DEFAULT_CENTER;
                const zoom = initialZoom !== null ? initialZoom : DEFAULT_ZOOM;

                mapInstanceRef.current = new google.maps.Map(mapRef.current, {
                    ...MAP_OPTIONS,
                    center,
                    zoom,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition.RIGHT_BOTTOM
                    }
                });

                infoWindowRef.current = new google.maps.InfoWindow({
                    disableAutoPan: true // Keep auto-panning disabled
                });

                const registeredInstance = registerMapInstance({
                    mapInstance: mapInstanceRef.current,
                    isFromVisualMap: true, // Indicate it's from the visual map component
                    orderId,
                    customerId,
                    orderlineIds,
                    panels: validPanels, // Pass the filtered valid panels
                    campaignType
                });

                if (onSaveMap && typeof onSaveMap === 'function') {
                    onSaveMap(registeredInstance);
                }
            }

            // Clear previous markers before adding new ones
            if (markersRef.current.length > 0) {
                 markersRef.current.forEach(marker => marker.map = null); // Remove from map
                 markersRef.current = [];
            }
             if (clustererRef.current) {
                clustererRef.current.clearMarkers(); // Clear markers from clusterer
             }

            const newMarkers = validPanels.map(panel => {
                const allStatuses = panel.statusInformation?.map(info => info.status?.toLowerCase()) || [];
                const uniqueStatuses = [...new Set(allStatuses)];
                let markerColor = "grey"; // Default color

                 if (uniqueStatuses.length === 1 && panel.statusInformation?.[0]?.color) {
                     markerColor = panel.statusInformation[0].color;
                 } else if (uniqueStatuses.length > 1) {
                     const hasPosted = allStatuses.includes(CLASSIC_PANEL_STATUS.POSTED);
                     const hasFailed = allStatuses.includes(CLASSIC_PANEL_STATUS.FAILED);
                     const hasLate = allStatuses.includes(CLASSIC_PANEL_STATUS.LATE) || allStatuses.includes(CLASSIC_PANEL_STATUS.POSTED_LATE);

                     if (hasPosted && (hasFailed || hasLate)) markerColor = 'pink'; 
                     else if (hasLate && hasFailed) markerColor = 'blue'; 
                     else markerColor = 'pink'; 

                 } else if (panel.statusInformation?.[0]?.color) {
                     // Fallback if logic above doesn't cover a case but color exists
                     markerColor = panel.statusInformation[0].color;
                 }


                const marker = new AdvancedMarkerElement({
                    position: { lat: panel.latitude, lng: panel.longitude },
                    content: createMarkerElement(markerColor), 
                    title: `Panel ID(s): ${panel.panelIdsAtLocation?.join(', ') || 'N/A'}\nAddress: ${panel.address || ''}`, 
                    zIndex: 1 
                });

                marker.addListener('click', () => {
                    if (!mapInstanceRef.current || !infoWindowRef.current) return;

                     mapInstanceRef.current.setZoom(Math.max(mapInstanceRef.current.getZoom() ?? DEFAULT_ZOOM, MAX_ZOOM_LEVEL - 2)); // Zoom in moderately

                    infoWindowRef.current.setContent(createInfoWindowContent(panel));
                    infoWindowRef.current.open({ map: mapInstanceRef.current, anchor: marker }); // Modern way to open InfoWindow
                });

                return marker;
            });

            markersRef.current = newMarkers;

             // Initialize or update MarkerClusterer
             if (newMarkers.length > 0) {
                 if (!clustererRef.current) {
                     clustererRef.current = new MarkerClusterer({
                         map: mapInstanceRef.current,
                         markers: newMarkers,
                         renderer: customRenderer, 
                     });
                 } else {
                     clustererRef.current.addMarkers(newMarkers);
                 }
             }

            if (newMarkers.length > 0 && !isLoadedMap && mapInstanceRef.current) {
                const bounds = new google.maps.LatLngBounds();
                newMarkers.forEach(marker => {
                    if (marker.position) bounds.extend(marker.position);
                });
                // Check if bounds are valid before fitting
                 if (!bounds.isEmpty()) {
                    mapInstanceRef.current.fitBounds(bounds);

                    // Add listener to prevent over-zooming after fitBounds completes
                    google.maps.event.addListenerOnce(mapInstanceRef.current, 'bounds_changed', () => {
                         if (mapInstanceRef.current && mapInstanceRef.current.getZoom() > MAX_ZOOM_LEVEL) {
                            mapInstanceRef.current.setZoom(MAX_ZOOM_LEVEL);
                        }
                    });
                } else {
                     console.warn("[Map Init] Could not fit bounds - bounds empty (check marker positions).");
                 }
            }

        } catch (error) {
            showError(`Error loading map: ${error.message || 'Unknown error'}`);
        }
    }, [
        campaignType, customerId, orderId, onSaveMap,
        initialCenter, initialZoom, isLoadedMap, 
        validPanels, 
        showError, 
        createInfoWindowContent, customRenderer
    ]);

    // Main effect hook to run initialization
    useEffect(() => {
        initializeMap();

        return () => {
            // Remove markers from map instance if they exist
            if (markersRef.current.length) {
                markersRef.current.forEach(marker => {
                    marker.map = null;
                });
                markersRef.current = [];
            }
            // Clear markers from clusterer instance
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
            }
        };
    }, [initializeMap]); 

    return (
        <div className={styles.wrapper}>
            <div
                id={styles.pod_panel_map_container_play}
                className={styles.map_container}
                ref={mapRef}
            />

            {campaignType === 'posting' && (
                <div className={styles.legend}>
                    <div className={styles.panelLegend}>
                        <span className={styles.legendDotGreen} />
                        <span>Posted</span>
                    </div>
                    <div className={styles.panelLegend}>
                        <span className={styles.legendDotPink} />
                        <span>Partially</span>
                    </div>
                    <div className={styles.panelLegend}>
                        <span className={styles.legendDotBlue} />
                        <span>Failed</span>
                    </div>
                    {campaignType === PRODUCT_TYPES.CLASSIC &&
                        <div className={styles.panelLegend}>
                            <span className={styles.legendDotPurple} />
                            <span>Late</span>
                        </div>
                    }
                    <div className={styles.panelLegend}>
                        <span className={styles.legendDotYellow} />
                        <span>Ongoing</span>
                    </div>
                    <div className={styles.panelLegend}>
                        <span className={styles.legendDotOrange} />
                        <span>Not Started</span>
                    </div>
                </div>
            )}
        </div>
    );
});

PanelMap.displayName = 'PanelMap';

export default PanelMap;